import { useQuery } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, useState } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { type ShopByLookPageQuery, type ShopByLookPageQueryVariables } from '../../__generated__/graphql-client-types';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { DEFAULT_META_DESCRIPTION } from '../../constants/general';
import { SHOP_BY_LOOK_LINK } from '../../constants/links';
import {
	EMPTY_TAG_FILTER_SETS,
	SectionTitleEnum,
	TRENDS_AND_INFLUENCES_TAG,
	TWENTY_ONE_RESULTS
} from '../../constants/project/shop-by-look';
import { QuickStartContextProvider } from '../../contexts/quickstart/quickstart.context';
import { convertQuickStartsToSimpleLooks, fillTagFilterArray } from '../../helpers/shop-by-look/shop-by-look.helper';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useShopByLookParams } from '../../hooks/shop-by-look/shop-by-look.hooks';
import { SHOP_BY_LOOK_PAGE } from '../../queries/shop-by-look/shop-by-look.queries';
import { ShopByLookHeader } from './components/shop-by-look-header/shop-by-look-header.component';
import {
	FeaturedLooks,
	MyFavorites,
	ShopByLookSectionWrapper,
	TrendsAndInfluences
} from './components/shop-by-look-section-wrapper/shop-by-look-section-wrapper.component';
import { ViewAllFavorites } from './components/view-all-favorites/view-all-favorites.component';

export const ShopByLookPage: FunctionComponent = () => {
	const pageName = 'shop-by-look';
	const dataLayer = {
		page: pageName
	};
	useTrackPageView({ pageName }, dataLayer);

	const { favorites, rooms, styles, update } = useShopByLookParams();
	const [, setPage] = useState(1);

	const tagFilterSetsArray = fillTagFilterArray(rooms, styles);
	const filterApplied = tagFilterSetsArray.length !== 0;

	const { data, previousData, error, fetchMore } = useQuery<ShopByLookPageQuery, ShopByLookPageQueryVariables>(SHOP_BY_LOOK_PAGE, {
		variables: {
			featuredInput: {
				tagFilterSets: filterApplied ? { include: tagFilterSetsArray } : { include: [{ name: 'featured', value: 'shop-by-look' }] },
				paging: { page: 1, pageSize: 9, useInfiniteScroll: Boolean(filterApplied) },
				showingFavoriteLooks: false
			},
			favoriteInput: {
				tagFilterSets: favorites ? { include: tagFilterSetsArray } : EMPTY_TAG_FILTER_SETS,
				paging: TWENTY_ONE_RESULTS,
				showingFavoriteLooks: true
			},
			trendsInput: {
				tagFilterSets: TRENDS_AND_INFLUENCES_TAG,
				paging: { page: 1, pageSize: 10 },
				showingFavoriteLooks: false
			}
		}
	});

	useDyRecommendationContext({ type: DyRecommendationContextType.OTHER_PAGE });

	// prevents spinner from showing on every refetch
	// found this to be a more pleasant UX
	const looksData = data || previousData;

	const favoriteLooks = looksData?.favoriteLooks?.quickStarts || [];
	const featuredLooks = looksData?.featuredLooks?.quickStarts || [];
	const trendsLooks = looksData?.trendsAndInfluences?.quickStarts || [];

	if (error) {
		return <FiveHundred />;
	}

	if (!looksData) {
		return <PageLoading />;
	}

	const updateFilterAndResetPage = (group: 'room' | 'style', value: string) => {
		setPage(1);
		update(group, value);
	};

	const fetchMoreLooks = () => {
		setPage((prevState) => {
			const newPage = prevState + 1;
			void fetchMore({
				variables: {
					featuredInput: {
						tagFilterSets: { include: tagFilterSetsArray },
						paging: { page: newPage, pageSize: 9, useInfiniteScroll: true },
						showingFavoriteLooks: false
					}
				}
			}).catch((err) => {
				logError(err);
				return prevState;
			});
			return newPage;
		});
	};

	const shouldShowMyFavoritesCarousel =
		looksData?.favoriteLooks?.quickStarts && looksData.favoriteLooks.quickStarts.length >= 1 && !filterApplied;
	const shouldShowViewAllFavorites = favorites && looksData?.favoriteLooks?.quickStarts;
	const shouldShowTrendsAndInfluences = !filterApplied;

	return (
		<QuickStartContextProvider>
			<PageContainer canonicalURL={SHOP_BY_LOOK_LINK} pageTitle="Shop By Look" metaDescription={DEFAULT_META_DESCRIPTION}>
				{shouldShowViewAllFavorites ? (
					<ViewAllFavorites
						looks={convertQuickStartsToSimpleLooks(favoriteLooks)}
						rooms={rooms}
						styles={styles}
						update={updateFilterAndResetPage}
					/>
				) : (
					<>
						<ShopByLookHeader update={updateFilterAndResetPage} rooms={rooms} styles={styles} />

						{shouldShowMyFavoritesCarousel && (
							<ShopByLookSectionWrapper
								sectionTitle={SectionTitleEnum.MY_FAVORITES}
								numLooks={convertQuickStartsToSimpleLooks(favoriteLooks).length}>
								<MyFavorites looks={convertQuickStartsToSimpleLooks(favoriteLooks)} />
							</ShopByLookSectionWrapper>
						)}

						<ShopByLookSectionWrapper sectionTitle={SectionTitleEnum.FEATURED_LOOKS}>
							<FeaturedLooks looks={convertQuickStartsToSimpleLooks(featuredLooks)} fetchMoreLooks={fetchMoreLooks} />
						</ShopByLookSectionWrapper>

						{shouldShowTrendsAndInfluences && (
							<ShopByLookSectionWrapper sectionTitle={SectionTitleEnum.TRENDS_AND_INFLUENCES}>
								<TrendsAndInfluences looks={convertQuickStartsToSimpleLooks(trendsLooks)} />
							</ShopByLookSectionWrapper>
						)}
					</>
				)}
			</PageContainer>
		</QuickStartContextProvider>
	);
};

export const RoutedShopByLookPage = configureRoute({ path: SHOP_BY_LOOK_LINK }, ShopByLookPage);
